import React, { useState } from "react";
import { motion } from "framer-motion";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import NavBar from "@/components/NavBar";
import PageHeader from "@/components/PageHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { toast } from "@/hooks/use-toast";
import {
  User,
  Bell,
  Shield,
  Camera,
  Save,
  Key,
  Trash2,
  Settings as SettingsIcon,
  Loader2,
  ArrowLeft,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import {
  useUserProfile,
  useUpdateUserProfile,
  useUserSettings,
  useUpdateUserSettings,
  useChangePassword,
} from "@/hooks/useApi";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";

const profileFormSchema = z.object({
  name: z.string().min(2, { message: "Name must be at least 2 characters." }),
  username: z
    .string()
    .min(2, { message: "Username must be at least 2 characters." }),
  email: z.string().email({ message: "Please enter a valid email address." }),
  bio: z
    .string()
    .max(160, { message: "Bio must not exceed 160 characters." })
    .optional(),
  location: z.string().optional(),
  website: z
    .string()
    .url({ message: "Please enter a valid URL." })
    .optional()
    .or(z.literal("")),
  expertise: z.string().optional(),
});

type ProfileFormValues = z.infer<typeof profileFormSchema>;

const notificationsFormSchema = z.object({
  emailNotifications: z.boolean().default(true),
  projectUpdates: z.boolean().default(true),
  newFeedback: z.boolean().default(true),
  validationResults: z.boolean().default(true),
  marketingEmails: z.boolean().default(false),
});

type NotificationsFormValues = z.infer<typeof notificationsFormSchema>;

const securityFormSchema = z
  .object({
    currentPassword: z
      .string()
      .min(1, { message: "Current password is required." }),
    newPassword: z
      .string()
      .min(8, { message: "Password must be at least 8 characters." }),
    confirmPassword: z
      .string()
      .min(8, { message: "Please confirm your new password." }),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type SecurityFormValues = z.infer<typeof securityFormSchema>;

const UserSettings: React.FC = () => {
  const [activeTab, setActiveTab] = useState("account");
  const { user: authUser, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Fetch real user data only if authenticated
  const {
    data: userProfile,
    isLoading: isProfileLoading,
    error: profileError,
  } = useUserProfile();

  const {
    data: userSettings,
    isLoading: isSettingsLoading,
    error: settingsError,
  } = useUserSettings();

  const updateProfileMutation = useUpdateUserProfile();
  const updateSettingsMutation = useUpdateUserSettings();
  const changePasswordMutation = useChangePassword();

  // Initialize form with real data or fallback values
  const defaultProfileValues: ProfileFormValues = {
    name: userProfile?.name || authUser?.name || "",
    username:
      userProfile?.username ||
      authUser?.name?.toLowerCase().replace(/\s+/g, "") ||
      "",
    email: userProfile?.email || authUser?.email || "",
    bio:
      userProfile?.bio ||
      "Product designer and idea validator. I help entrepreneurs determine if their ideas are worth pursuing.",
    location: userProfile?.location || "San Francisco, CA",
    website: userProfile?.website || "",
    expertise:
      userProfile?.expertise || "Product Design, UX Research, Market Analysis",
  };

  const defaultNotificationValues: NotificationsFormValues = {
    emailNotifications: userSettings?.emailNotifications ?? true,
    projectUpdates: userSettings?.projectUpdates ?? true,
    newFeedback: userSettings?.newFeedback ?? true,
    validationResults: userSettings?.validationResults ?? true,
    marketingEmails: userSettings?.marketingEmails ?? false,
  };

  const profileForm = useForm<ProfileFormValues>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: defaultProfileValues,
    mode: "onChange",
  });

  const notificationsForm = useForm<NotificationsFormValues>({
    resolver: zodResolver(notificationsFormSchema),
    defaultValues: defaultNotificationValues,
  });

  const securityForm = useForm<SecurityFormValues>({
    resolver: zodResolver(securityFormSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  // Update form values when data loads
  React.useEffect(() => {
    if (userProfile) {
      profileForm.reset({
        name: userProfile.name,
        username:
          userProfile.username ||
          userProfile.name.toLowerCase().replace(/\s+/g, ""),
        email: userProfile.email,
        bio: userProfile.bio || "",
        location: userProfile.location || "",
        website: userProfile.website || "",
        expertise: userProfile.expertise || "",
      });
    }
  }, [userProfile, profileForm]);

  React.useEffect(() => {
    if (userSettings) {
      notificationsForm.reset({
        emailNotifications: userSettings.emailNotifications,
        projectUpdates: userSettings.projectUpdates,
        newFeedback: userSettings.newFeedback,
        validationResults: userSettings.validationResults,
        marketingEmails: userSettings.marketingEmails,
      });
    }
  }, [userSettings, notificationsForm]);

  const onProfileSubmit = (data: ProfileFormValues) => {
    updateProfileMutation.mutate(data);
  };

  const onNotificationsSubmit = (data: NotificationsFormValues) => {
    updateSettingsMutation.mutate(data);
  };

  const onSecuritySubmit = (data: SecurityFormValues) => {
    changePasswordMutation.mutate(
      {
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      },
      {
        onSuccess: () => {
          securityForm.reset();
        },
      }
    );
  };

  // Show authentication required state
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-primary/5">
        <NavBar />
        <div className="container mx-auto px-4 pt-20 sm:pt-24 pb-16">
          <div className="max-w-4xl mx-auto">
            <div className="text-center py-12 sm:py-20">
              <div className="text-4xl sm:text-6xl mb-4">🔐</div>
              <h3 className="text-xl sm:text-2xl font-bold mb-4 text-gray-800">
                Authentication Required
              </h3>
              <p className="text-sm sm:text-base text-gray-600 mb-8 max-w-md mx-auto">
                Please sign in to access your account settings.
              </p>
              <Button asChild className="bg-primary hover:bg-primary/90">
                <Link to="/login">Sign In</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show loading state
  if (isProfileLoading || isSettingsLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-primary/5">
        <NavBar />
        <div className="container mx-auto px-4 pt-20 sm:pt-24 pb-16">
          <div className="max-w-4xl mx-auto">
            <div className="text-center py-12 sm:py-20">
              <Loader2 className="w-6 h-6 sm:w-8 sm:h-8 animate-spin mx-auto mb-4 text-primary" />
              <p className="text-sm sm:text-base text-gray-600">
                Loading your settings...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (profileError || settingsError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-primary/5">
        <NavBar />
        <div className="container mx-auto px-4 pt-20 sm:pt-24 pb-16">
          <div className="max-w-4xl mx-auto">
            <div className="text-center py-12 sm:py-20">
              <div className="text-red-500 text-4xl sm:text-6xl mb-4">⚠️</div>
              <h3 className="text-xl sm:text-2xl font-bold mb-4 text-gray-800">
                Failed to load settings
              </h3>
              <p className="text-sm sm:text-base text-gray-600 mb-8">
                {profileError instanceof Error
                  ? profileError.message
                  : settingsError instanceof Error
                  ? settingsError.message
                  : "Please try again later."}
              </p>
              <Button onClick={() => window.location.reload()}>Retry</Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Use real settings data or fallback to defaults
  const settings = userSettings || {
    emailNotifications: {
      campaignUpdates: true,
      newComments: true,
      weeklyDigest: false,
      marketingEmails: false,
    },
    privacy: {
      profileVisibility: "public",
      showEmail: false,
      allowDirectMessages: true,
    },
    preferences: {
      language: "en",
      timezone: "UTC",
      theme: "system",
    },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-primary/5">
      <NavBar />
      <div className="container mx-auto px-4 pt-20 sm:pt-24 pb-16">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="text-slate-600 hover:text-slate-900 hover:bg-slate-100"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <PageHeader
              title="Settings"
              description="Manage your account settings and preferences"
            />
          </motion.div>

          <Card className="bg-white/95 backdrop-blur-sm border-0 shadow-2xl rounded-2xl overflow-hidden mt-6 sm:mt-8">
            <Tabs defaultValue="profile" className="w-full">
              <div className="bg-gradient-to-r from-gray-50 to-blue-50/50 border-b border-gray-100">
                <TabsList className="w-full bg-transparent border-0 p-0 h-auto rounded-none overflow-x-auto">
                  <TabsTrigger
                    value="profile"
                    className="flex-1 py-3 sm:py-4 lg:py-6 px-4 sm:px-6 lg:px-8 text-sm sm:text-base lg:text-lg font-semibold data-[state=active]:bg-white data-[state=active]:shadow-lg data-[state=active]:border-b-4 data-[state=active]:border-primary-500 rounded-none data-[state=active]:text-primary-600 whitespace-nowrap flex-shrink-0"
                  >
                    <User
                      size={14}
                      className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2"
                    />
                    Profile
                  </TabsTrigger>
                  <TabsTrigger
                    value="notifications"
                    className="flex-1 py-3 sm:py-4 lg:py-6 px-4 sm:px-6 lg:px-8 text-sm sm:text-base lg:text-lg font-semibold data-[state=active]:bg-white data-[state=active]:shadow-lg data-[state=active]:border-b-4 data-[state=active]:border-primary-500 rounded-none data-[state=active]:text-primary-600 whitespace-nowrap flex-shrink-0"
                  >
                    <Bell
                      size={14}
                      className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2"
                    />
                    Notifications
                  </TabsTrigger>
                  <TabsTrigger
                    value="security"
                    className="flex-1 py-3 sm:py-4 lg:py-6 px-4 sm:px-6 lg:px-8 text-sm sm:text-base lg:text-lg font-semibold data-[state=active]:bg-white data-[state=active]:shadow-lg data-[state=active]:border-b-4 data-[state=active]:border-primary-500 rounded-none data-[state=active]:text-primary-600 whitespace-nowrap flex-shrink-0"
                  >
                    <Shield
                      size={14}
                      className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2"
                    />
                    Security
                  </TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="profile" className="p-4 sm:p-6 lg:p-8">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 mb-6 sm:mb-8">
                  <div className="relative self-start">
                    <Avatar className="h-16 w-16 sm:h-20 sm:w-20 ring-4 ring-primary/20">
                      <AvatarImage
                        src={userProfile?.avatarUrl}
                        alt={userProfile?.name}
                      />
                      <AvatarFallback className="bg-gradient-to-r from-primary to-primary-600 text-white text-lg sm:text-xl font-semibold">
                        {userProfile?.name?.charAt(0) ||
                          authUser?.name?.charAt(0) ||
                          "U"}
                      </AvatarFallback>
                    </Avatar>
                    <Button
                      size="sm"
                      variant="outline"
                      className="absolute -bottom-2 -right-2 h-6 w-6 sm:h-8 sm:w-8 rounded-full p-0"
                    >
                      <Camera className="h-3 w-3 sm:h-4 sm:w-4" />
                    </Button>
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-semibold">
                      {userProfile?.name || authUser?.name || "User"}
                    </h3>
                    <p className="text-sm sm:text-base text-muted-foreground">
                      {userProfile?.email || authUser?.email}
                    </p>
                  </div>
                </div>

                <Form {...profileForm}>
                  <form
                    onSubmit={profileForm.handleSubmit(onProfileSubmit)}
                    className="space-y-4 sm:space-y-6"
                  >
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                      <FormField
                        control={profileForm.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Full Name</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Enter your full name"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={profileForm.control}
                        name="username"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Username</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Enter your username"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={profileForm.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Enter your email"
                              type="email"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={profileForm.control}
                      name="bio"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Bio</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="Tell us about yourself"
                              className="resize-none"
                              {...field}
                            />
                          </FormControl>
                          <FormDescription>
                            Brief description for your profile.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                      <FormField
                        control={profileForm.control}
                        name="location"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Location</FormLabel>
                            <FormControl>
                              <Input placeholder="City, Country" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={profileForm.control}
                        name="website"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Website</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="https://example.com"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={profileForm.control}
                      name="expertise"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Areas of Expertise</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="e.g., Product Design, UX Research, Market Analysis"
                              {...field}
                            />
                          </FormControl>
                          <FormDescription>
                            Your professional skills and expertise areas.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="flex justify-end">
                      <Button
                        type="submit"
                        disabled={updateProfileMutation.isPending}
                        className="bg-primary hover:bg-primary/90 text-sm sm:text-base"
                      >
                        {updateProfileMutation.isPending ? (
                          <>
                            <Loader2 className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4 animate-spin" />
                            <span className="hidden sm:inline">Saving...</span>
                            <span className="sm:hidden">Save</span>
                          </>
                        ) : (
                          <>
                            <Save className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                            <span className="hidden sm:inline">
                              Save Changes
                            </span>
                            <span className="sm:hidden">Save</span>
                          </>
                        )}
                      </Button>
                    </div>
                  </form>
                </Form>
              </TabsContent>

              <TabsContent value="notifications" className="p-4 sm:p-6 lg:p-8">
                <div className="mb-4 sm:mb-6">
                  <h3 className="text-base sm:text-lg font-semibold mb-2">
                    Notification Preferences
                  </h3>
                  <p className="text-sm sm:text-base text-muted-foreground">
                    Choose how you want to be notified about updates and
                    activities.
                  </p>
                </div>

                <Form {...notificationsForm}>
                  <form
                    onSubmit={notificationsForm.handleSubmit(
                      onNotificationsSubmit
                    )}
                    className="space-y-4 sm:space-y-6"
                  >
                    <div className="space-y-3 sm:space-y-4">
                      <FormField
                        control={notificationsForm.control}
                        name="emailNotifications"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 sm:p-4">
                            <div className="space-y-0.5 flex-1 mr-3">
                              <FormLabel className="text-sm sm:text-base">
                                Email Notifications
                              </FormLabel>
                              <FormDescription className="text-xs sm:text-sm">
                                Receive notifications via email
                              </FormDescription>
                            </div>
                            <FormControl>
                              <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={notificationsForm.control}
                        name="projectUpdates"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 sm:p-4">
                            <div className="space-y-0.5 flex-1 mr-3">
                              <FormLabel className="text-sm sm:text-base">
                                Project Updates
                              </FormLabel>
                              <FormDescription className="text-xs sm:text-sm">
                                Get notified when your campaigns receive updates
                              </FormDescription>
                            </div>
                            <FormControl>
                              <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={notificationsForm.control}
                        name="newFeedback"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 sm:p-4">
                            <div className="space-y-0.5 flex-1 mr-3">
                              <FormLabel className="text-sm sm:text-base">
                                New Feedback
                              </FormLabel>
                              <FormDescription className="text-xs sm:text-sm">
                                Notify when someone provides feedback on your
                                campaigns
                              </FormDescription>
                            </div>
                            <FormControl>
                              <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={notificationsForm.control}
                        name="validationResults"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 sm:p-4">
                            <div className="space-y-0.5 flex-1 mr-3">
                              <FormLabel className="text-sm sm:text-base">
                                Validation Results
                              </FormLabel>
                              <FormDescription className="text-xs sm:text-sm">
                                Receive updates on campaign validation progress
                              </FormDescription>
                            </div>
                            <FormControl>
                              <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={notificationsForm.control}
                        name="marketingEmails"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 sm:p-4">
                            <div className="space-y-0.5 flex-1 mr-3">
                              <FormLabel className="text-sm sm:text-base">
                                Marketing Emails
                              </FormLabel>
                              <FormDescription className="text-xs sm:text-sm">
                                Receive promotional content and updates
                              </FormDescription>
                            </div>
                            <FormControl>
                              <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="flex justify-end">
                      <Button
                        type="submit"
                        disabled={updateSettingsMutation.isPending}
                        className="bg-primary hover:bg-primary/90 text-sm sm:text-base"
                      >
                        {updateSettingsMutation.isPending ? (
                          <>
                            <Loader2 className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4 animate-spin" />
                            <span className="hidden sm:inline">Saving...</span>
                            <span className="sm:hidden">Save</span>
                          </>
                        ) : (
                          <>
                            <Save className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                            <span className="hidden sm:inline">
                              Save Preferences
                            </span>
                            <span className="sm:hidden">Save</span>
                          </>
                        )}
                      </Button>
                    </div>
                  </form>
                </Form>
              </TabsContent>

              <TabsContent value="security" className="p-4 sm:p-6 lg:p-8">
                <div className="mb-4 sm:mb-6">
                  <h3 className="text-base sm:text-lg font-semibold mb-2">
                    Security Settings
                  </h3>
                  <p className="text-sm sm:text-base text-muted-foreground">
                    Manage your password and account security.
                  </p>
                </div>

                <Form {...securityForm}>
                  <form
                    onSubmit={securityForm.handleSubmit(onSecuritySubmit)}
                    className="space-y-4 sm:space-y-6"
                  >
                    <FormField
                      control={securityForm.control}
                      name="currentPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Current Password</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Enter your current password"
                              type="password"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                      <FormField
                        control={securityForm.control}
                        name="newPassword"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>New Password</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Enter new password"
                                type="password"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={securityForm.control}
                        name="confirmPassword"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Confirm New Password</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Confirm new password"
                                type="password"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="flex justify-end">
                      <Button
                        type="submit"
                        disabled={changePasswordMutation.isPending}
                        className="bg-primary hover:bg-primary/90 text-sm sm:text-base"
                      >
                        {changePasswordMutation.isPending ? (
                          <>
                            <Loader2 className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4 animate-spin" />
                            <span className="hidden sm:inline">
                              Changing...
                            </span>
                            <span className="sm:hidden">Change</span>
                          </>
                        ) : (
                          <>
                            <Key className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                            <span className="hidden sm:inline">
                              Change Password
                            </span>
                            <span className="sm:hidden">Change</span>
                          </>
                        )}
                      </Button>
                    </div>
                  </form>
                </Form>

                <Separator className="my-6 sm:my-8" />

                <div className="space-y-3 sm:space-y-4">
                  <div>
                    <h4 className="text-base sm:text-lg font-semibold text-red-600 mb-2">
                      Danger Zone
                    </h4>
                    <p className="text-sm sm:text-base text-muted-foreground mb-4">
                      Once you delete your account, there is no going back.
                      Please be certain.
                    </p>
                  </div>

                  <Button
                    variant="destructive"
                    className="bg-red-600 hover:bg-red-700 text-sm sm:text-base"
                  >
                    <Trash2 className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                    <span className="hidden sm:inline">Delete Account</span>
                    <span className="sm:hidden">Delete</span>
                  </Button>
                </div>
              </TabsContent>
            </Tabs>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default UserSettings;
